import { ApiSystem, Asset } from "../state/types";
import { resolveCameraDirection } from "./assetCameraDirection";

type AssetWithDynamicImages = Asset & Record<string, any>;

const PRIMARY_BGR_IMAGE_FIELDS = [
  "imageBgr",
  "imageBgrPano",
  "imageBgBlur",
  "imageBgBlurExterior",
  "imageBgblur",
  "imageBgrBlur",
  "imageBlur",
];

const FALLBACK_BGR_IMAGE_FIELDS = [
  "imageBgrExterior",
  "imageBgrManual",
];

function readImageArray(asset: AssetWithDynamicImages, field: string): any[] {
  const value = asset[field];
  return Array.isArray(value) ? value : [];
}

function dedupeImages(images: any[]): any[] {
  const seen = new Set<string>();

  return images.filter((image) => {
    const key = image?.imageUrlCf || image?.imageUrl || JSON.stringify(image);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Thumbnail to show on asset list/grid views.
 *  Picks the front-corner camera based on the asset's config camera direction
 *  (CW → Right Front, CCW → Left Front) and prefers BGR > Cropped > Raw exterior.
 *  Falls back to `asset.thumbnail` when no matching image is found. */
export function getDirectionalThumbnail(
  asset: Asset | null | undefined,
  systems: ApiSystem[] | null | undefined,
): string {
  if (!asset) return '';
  const fallback = asset.thumbnail || '';

  const direction = resolveCameraDirection(asset, systems);
  const targetTag = direction === 'cw' ? 'Right Front' : 'Left Front';

  const findSrcByTag = (images: any): string | null => {
    if (!Array.isArray(images)) return null;
    const match = images.find((img: any) => img?.imageTag === targetTag);
    return match?.imageUrlCf || match?.imageUrl || null;
  };

  const src =
    findSrcByTag(getBgrCarouselImages(asset)) ||
    findSrcByTag((asset as any).imageCrop) ||
    findSrcByTag(asset.imageExterior);

  if (!src) return fallback;
  return src.replace(/(\.[^./\?]+)$/, '_thumbnail.jpg');
}

/** Pano images to display: the background-replaced pano when it exists,
 *  otherwise the raw pano. Single rule for every pano view (asset page
 *  carousel, iframe /pano route, iframe alias pano imageSet) so a BGR pano
 *  always supersedes the raw capture it was made from. */
export function getPanoCarouselImages(asset: Asset | null | undefined): any[] {
  if (!asset) return [];

  const dynamicAsset = asset as AssetWithDynamicImages;
  const bgrPano = readImageArray(dynamicAsset, "imageBgrPano");
  return bgrPano.length > 0 ? bgrPano : readImageArray(dynamicAsset, "imagePano");
}

export function getBgrCarouselImages(asset: Asset | null | undefined): any[] {
  if (!asset) return [];

  const dynamicAsset = asset as AssetWithDynamicImages;
  const primaryImages = PRIMARY_BGR_IMAGE_FIELDS.flatMap((field) => readImageArray(dynamicAsset, field));
  const fallbackImages = primaryImages.length > 0
    ? []
    : FALLBACK_BGR_IMAGE_FIELDS.flatMap((field) => readImageArray(dynamicAsset, field));

  return dedupeImages([...primaryImages, ...fallbackImages]);
}
